import { useEffect, useState } from 'react';

import {
    Grid, Typography, Button, Dialog, DialogContent, Divider,
    DialogActions, Collapse, CircularProgress
} from '@material-ui/core';

import { Alert } from '@material-ui/lab';

import FormField from '../../FormField';

import requestServiceDocumentService from '../../../domain/services/request/service/request.service.document.service';
import loginService from '../../../domain/services/login.service';

const RequestDocumentsServiceConfigModal = ({ documents, request, handleClose, onConfirm, isEdit }) => {

    const initialState = () => {
        const firstDocument = documents[0];

        if (!firstDocument)
            return {};

        const initialConfig = {
            isVisibleCustomer: firstDocument.isVisibleCustomer,
            isServiceMainDocument: firstDocument.isServiceMainDocument,
            serviceId: isEdit ? firstDocument.serviceId || 0 : request.serviceId,
            requestId: firstDocument.requestId ?? request.id
        }

        if (isEdit) {
            const nameArray = firstDocument.name.split(".");
            nameArray.pop();

            initialConfig.name = nameArray.join(".");
        }

        return initialConfig;
    }

    const [serviceConfig, setServiceConfig] = useState(initialState());
    const [standardName, setStandardName] = useState(null);

    const [requestServices, setRequestServices] = useState([])

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => { getRequestServices(request.mainId || request.id) }, []);

    useEffect(() => {
        const selected = requestServices.find(i => i.value === serviceConfig.serviceId) || {};
        const isMainAndVisible = serviceConfig.isVisibleCustomer && serviceConfig.isServiceMainDocument;
        const canonicalBase = isMainAndVisible ? selected.canonicalDocumentName : null;
        if (!canonicalBase) {
            setStandardName(null);
            return;
        }
        const firstDocName = (documents[0] && documents[0].name) || '';
        const nameParts = firstDocName.split('.');
        const extension = nameParts.length > 1 ? nameParts.pop() : '';
        setStandardName(extension ? `${canonicalBase}.${extension}` : canonicalBase);
    }, [serviceConfig, requestServices, documents]);

    const getRequestServices = async (requestId) =>
        requestServiceDocumentService
            .getRequestServices(requestId)
            .then(setRequestServices)
            .finally(() => setIsLoading(false));


    const handleConfirm = async () =>
        Promise
            .resolve(setIsLoading(true))
            .then(() => {
                const configs = documents.map((doc, index) => {
                    const finalConfig = getFinalConfig(doc);
                    const getDocumentName = (doc, index) => {
                        if (finalConfig.name) return finalConfig.name;
                        if (isEdit) return doc.name;
                        if (!standardName) return doc.name;
                        if (documents.length > 1) return `${standardName} - ${index + 1}`;
                        return standardName;
                    };

                    const documentName = getDocumentName(doc, index);
                    return {
                        document: doc,
                        data: {
                            ...finalConfig,
                            name: documentName
                        }
                    }
                });
                return onConfirm(configs);
            })
            .finally(() => setIsLoading(false));

    const getFinalConfig = (document) => {
        const config = { ...serviceConfig }

        if (config.name) {
            const extension = document.name.split('.').pop();
            config.name = config.name + '.' + extension;
        }

        return config;
    }

    const handleChange = (field, value, checkMainDocument) => {
        if (!checkMainDocument) {
            setServiceConfig({ ...serviceConfig, [field]: value });
            return;
        }

        const newConfig = { ...serviceConfig, [field]: value };

        const isServiceMainDocument = checkMainDocumentValue(newConfig);

        setServiceConfig({ ...newConfig, isServiceMainDocument });
    }

    const checkMainDocumentValue = (newConfig) =>
        (!newConfig.isVisibleCustomer || !newConfig.serviceId) ? false : newConfig.isServiceMainDocument

    const handleChangeService = (field, value) => {
        const newValue = value == 0 ? null : value;

        handleChange(field, newValue, true);
    }

    const getSelectedService = () =>
        requestServices.find(i => i.value == serviceConfig.serviceId) || {};

    const getMainDocumentCheckbox = () => {
        const serviceWithoutMainDocument = !getSelectedService().isDocumentRequired;

        if (!serviceConfig.serviceId || serviceWithoutMainDocument || !serviceConfig.isVisibleCustomer)
            return null;

        return (
            <Grid item>
                <FormField
                    name="isServiceMainDocument" field="Documento principal" type='checkbox'
                    currentValue={serviceConfig.isServiceMainDocument} clean
                    titleColor='black' titleSize={14} onChange={handleChange}
                    placeholder={`O documento é o(a) ${getSelectedService().name}?`}
                />
            </Grid>
        )
    }

    const getNameField = () => {
        if (!isEdit && !standardName)
            return null;

        const stripExtension = (name) => {
            if (!name) return name;
            const parts = name.split('.');
            if (parts.length <= 1) return name;
            parts.pop();
            return parts.join('.');
        };

        const initialName = serviceConfig.name || stripExtension(standardName);

        return (
            <FormField
                name="name" currentValue={initialName}
                clean titleColor='black' titleSize={14} required
                field="Nome do documento"
                helperText={standardName ? `Padrão do serviço: ${standardName}. Edite só se necessário.` : null}
                onChange={handleChange}
            />
        )
    }

    const getServiceSelection = () => {
        const serviceName = getSelectedService().name || '-'

        if (!loginService.isInternal())
            return (
                <Grid item>
                    <Alert severity='info'>
                        Documento do serviço de <strong>{serviceName}</strong>.
                    </Alert>
                </Grid>
            )

        return (
            <FormField
                name="serviceId" currentValue={(serviceConfig.serviceId || 0)}
                clean titleColor='black' titleSize={14} type='select'
                options={requestServices} field="Serviço do documento"
                onChange={handleChangeService}
            />
        )
    }

    return (
        <Dialog
            open={true} disableEscapeKeyDown
            maxWidth="xs" fullWidth onClose={handleClose}
        >
            <DialogContent>
                <Grid container direction='column' spacing={4}>

                    <Grid item>
                        <Typography variant='h5'>
                            Configurações de serviço
                        </Typography>
                        <Typography variant='subtitle1' color='textSecondary'>
                            {documents.length} documento{documents.length > 1 ? 's' : ''} selecionado{documents.length > 1 ? 's' : ''}
                        </Typography>
                    </Grid>

                    {getServiceSelection()}

                    <Grid item>
                        <FormField
                            name="isVisibleCustomer" titleColor='black' titleSize={14} type='checkbox'
                            field={serviceConfig.serviceId ? "Versão final" : "Visível para o cliente"}
                            currentValue={serviceConfig.isVisibleCustomer} clean
                            onChange={(f, v) => handleChange(f, v, true)}
                        />

                        <Collapse in={serviceConfig.isVisibleCustomer}>
                            <Alert severity='warning'>
                                {!serviceConfig.serviceId ?
                                    `O documento ficará disponível para o cliente 
                                    imediatamente! Esse recurso só deve ser usado 
                                    caso seja necessário mostrar documentos para 
                                    o cliente durante a produção dos serviços`
                                    : `O documento ficará disponível para o cliente 
                                    quando todos os serviços forem concluídos.`
                                }
                            </Alert>
                        </Collapse>
                    </Grid>

                    {getMainDocumentCheckbox()}

                    {getNameField()}

                </Grid>
            </DialogContent>

            <Divider />

            <DialogActions>
                <Button
                    variant="text"
                    onClick={handleClose}
                    disabled={isLoading}
                >
                    Cancelar
                </Button>

                <Button
                    variant="contained" disabled={isLoading}
                    color='primary' onClick={handleConfirm}
                >
                    {isLoading && (
                        <CircularProgress color='secondary'
                            size={18} style={{ marginRight: 8 }} />
                    )}

                    {isLoading ? 'Aguarde' : 'Confirmar'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default RequestDocumentsServiceConfigModal;